import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useFilingCabinetContext } from '@/Contexts/Authenticated/FilingCabinetContext';
import ROW2View from '../../../Pages/ROW2View/ROW2View';

interface ROW2RouteProps {
    url: string;
    title: string;
    usesFilingCabinet?: boolean;
}

const ROW2Route = ({
    url,
    title,
    usesFilingCabinet,
}: ROW2RouteProps): React.ReactElement => {
    const { t } = useTranslation();
    const { generateNewID, loadByPath, removeActive } = useFilingCabinetContext();
    const { add } = useFilingCabinetContext();
    const [searchParams, setSearchParams] = useSearchParams();
    const { pathname: currentPath, search } = useLocation();
    const fcKey = searchParams.get('fc');
    const filingCabinetKey = fcKey ? Number.parseInt(fcKey) : undefined;
    const useFilingCabinet = usesFilingCabinet ?? true;

    const urlEdited = `${url}${search}&hideMenu=1`;

    // IMPORTANT: This is a semi-copy of what we do for the regular Razor routes.
    //            A couple of differences are specific to ROW2 functionality
    useEffect(() => {
        // Ignore if not using filing cabinet
        if (!useFilingCabinet) {
            return;
        }

        // Try to find existing filingcabinet item if reuse is enabled
        const filingCabinetKeyToReUse = loadByPath(currentPath)?.ID;
        if (filingCabinetKeyToReUse) {
            setSearchParams((prev) => {
                const params = prev;
                params.delete('fc');
                params.append('fc', filingCabinetKeyToReUse.toString());
                return params;
            }, { replace: true });
        }

        // If already a filing cabinet key present in URL, nothing required
        if (filingCabinetKey) {
            const item = {
                ID: filingCabinetKey,
                path: `${window.location.pathname}${window.location.search.replace(/[&?]fc=[0-9]+/, '')}`,
                active: true,
                title: t(title),
                state: '',
                isLocked: false,
            };
            add(item);
            return;
        }
        // Else generate new ID and navigate to it
        if (filingCabinetKeyToReUse) {
            return;
        }

        setSearchParams((prev) => {
            const params = prev;
            params.delete('fc');
            params.append('fc', generateNewID().toString());
            return params;
        }, { replace: true });
    }, [usesFilingCabinet, searchParams]);

    const keyUpHandler = (e: KeyboardEvent): void => {
        if (e.key === 'ESC') {
            removeActive();
        }
    };

    useEffect(() => {
        document.addEventListener('keyup', keyUpHandler);
        return () => {
            document.removeEventListener('keyup', keyUpHandler);
        };
    }, []);

    return (
        <ROW2View url={urlEdited} />
    );
};

export default ROW2Route;
